import { useCallback, useMemo, useState } from 'react';

import { useRouteAndQueryParams, useStack9 } from '@april9au/stack9-react';
import * as ui from '@april9au/stack9-ui';
import { Descriptions, MenuProps, Modal, notification, Tag } from 'antd';
import dayjs from 'dayjs';

import { ButtonLink } from '../components/ButtonLink/ButtonLink';
import HealthScreenDrawer from '../components/HealthScreen/HealthScreenDrawer';
import { HealthScreenQuestionsListView } from '../components/HealthScreen/HealthScreenQuestionsListView';
import { AppRoutes } from '../constants/appRoutes';
import { useTabs } from '../hooks/useTabs';
import { HealthScreenDetailScreen } from '../models/HealthScreen';

const S9PageSection = ui.S9PageSection.default;
const S9CustomEntityFormPage = ui.S9CustomEntityFormPage.default;
const S9Button = ui.S9Button.default;
const S9Skeleton = ui.Skeleton.default;
const S9Tabs = ui.S9Tabs.default;

const { confirm } = Modal;

const getColor = (rating: 'Green' | 'Amber' | 'Red' | null | undefined) => {
  if (rating === 'Green') {
    return '#008000';
  }

  if (rating === 'Amber') {
    return '#FFBF00';
  }

  if (rating === 'Red') {
    return '#FF0000';
  }

  return undefined;
};

export const HealthScreenDetail = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { currentTab, onChangeTab } = useTabs();
  const {
    routeParams: { id = '' },
    queryParams: { callbackUrl = '' },
  } = useRouteAndQueryParams();
  const { entityService } = useStack9();

  const {
    data,
    isValidating: isLoading,
    mutate,
  } = ui.useScreenQueryById<HealthScreenDetailScreen>(
    'getHealthScreenById',
    +id,
  );

  const [isLoadingAction, setIsLoadingAction] = useState(false);

  const handleAlert = useCallback(
    async (id: number) => {
      setIsLoadingAction(true);

      try {
        if (isLoadingAction) {
          return;
        }

        if (!id) {
          throw new Error('Invalid ID');
        }

        const { data } = await entityService.postWebhook<{
          code: number;
          message: string;
        }>('health_screen', `send-alert`, {
          entityId: id,
        });

        if (data.code === 200) {
          notification.success(data);
        } else {
          notification.error(data);
        }
      } catch (e) {
        notification.error({
          message: `Error: ${String(e)}`,
        });
      } finally {
        setIsLoadingAction(false);
      }
    },
    [isLoadingAction, entityService],
  );

  const handleRescreen = useCallback(
    async (id: number) => {
      setIsLoadingAction(true);

      try {
        if (isLoadingAction) {
          return;
        }

        if (!id) {
          throw new Error('Invalid ID');
        }

        const { data } = await entityService.postWebhook<{
          code: number;
          message: string;
        }>('health_screen', `send-rescreen`, {
          entityId: id,
        });

        if (data.code === 200) {
          notification.success(data);
        } else {
          notification.error(data);
        }

        mutate();
      } catch (e) {
        notification.error({
          message: `Error: ${String(e)}`,
        });
      } finally {
        setIsLoadingAction(false);
      }
    },
    [isLoadingAction, entityService, mutate],
  );

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'resend_alert',
        onClick: () => handleAlert(+id),
        label: <>Resend alert</>,
      },
      {
        key: 'rescreen',
        onClick: () =>
          confirm({
            title: 'Are you sure?',
            content: <>Confirm action?</>,
            okText: 'Send',
            okButtonProps: {
              type: 'primary',
            },
            cancelText: 'Cancel',
            onOk() {
              handleRescreen(+id);
            },
          }),
        label: <>Send rescreen request</>,
      },
    ],
    [handleRescreen, id],
  );

  return (
    <S9CustomEntityFormPage
      entityKey="health_screen"
      screenQueryName="getHealthScreenById"
      entityId={+id}
      title={`#${id}`}
      cancelLink={callbackUrl || AppRoutes.HealthScreenList}
      actionMenuItems={menuItems}
    >
      <S9Tabs
        defaultActiveKey={currentTab}
        onChange={onChangeTab}
        items={[
          {
            key: 'details',
            label: 'Details',
            children: isLoading ? (
              <S9Skeleton active />
            ) : (
              data && (
                <>
                  <S9PageSection
                    size="sm"
                    title=" " // Actions only show with a title
                    actions={
                      <>
                        <S9Button
                          label={`${data ? 'Edit' : 'Add'}`}
                          icon={`${data ? 'EditOutlined' : 'PlusOutlined'}`}
                          type="link"
                          onClick={() => setIsDrawerOpen(true)}
                        />
                      </>
                    }
                  >
                    <Descriptions
                      bordered
                      column={2}
                      items={[
                        {
                          label: 'Form type',
                          children: `${data.form_type}`,
                          span: 1,
                        },
                        {
                          label: 'Screening date',
                          children: data.screening_dt
                            ? dayjs(data.screening_dt).format(
                                'DD/MM/YYYY HH:mm',
                              )
                            : '-',
                          span: 1,
                        },
                        {
                          label: 'Client',
                          children: data.client.full_name,
                          span: 1,
                        },
                        {
                          label: 'Health practitioner',
                          children: data.practitioner,
                          span: 1,
                        },
                        ...(data.original_screen_id
                          ? [
                              {
                                label: 'Original health screen',
                                children: (
                                  <ButtonLink
                                    type="link"
                                    label={`#${data.original_screen_id}`}
                                    route={AppRoutes.HealthScreenDetail}
                                    params={{ id: data.original_screen_id }}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(data.form_type === 'Clinic Form'
                          ? [
                              {
                                label: 'Coaching opt-in',
                                children: data.coaching_opt_in ? 'Yes' : 'No',
                                span: 1,
                              },
                            ]
                          : []),
                      ]}
                    />
                  </S9PageSection>
                  <S9PageSection size="sm" title="Scores">
                    <Descriptions
                      column={4}
                      items={[
                        {
                          label: 'Depression score',
                          children: (
                            <Tag color={getColor(data.depression_rating)}>
                              {data.depression_score}
                            </Tag>
                          ),
                        },
                        {
                          label: 'Anxiety score',
                          children: (
                            <Tag color={getColor(data.anxiety_rating)}>
                              {data.anxiety_score}
                            </Tag>
                          ),
                        },
                        {
                          label: 'Stress score',
                          children: (
                            <Tag color={getColor(data.stress_rating)}>
                              {data.stress_score}
                            </Tag>
                          ),
                        },
                        {
                          label: 'Overall score',
                          children: (
                            <Tag color={getColor(data.overall_rating)}>
                              {data.overall_score}
                            </Tag>
                          ),
                        },
                      ]}
                    />
                  </S9PageSection>
                </>
              )
            ),
          },
          {
            key: 'qa',
            label: 'Questions & Answers',
            children: <HealthScreenQuestionsListView healthScreenId={+id} />,
          },
        ]}
      />
      <HealthScreenDrawer
        open={isDrawerOpen}
        id={data?.id}
        onSaved={() => {
          setIsDrawerOpen(false);
          mutate();
        }}
        onDeleted={() => {
          setIsDrawerOpen(false);
          mutate();
        }}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
      />
    </S9CustomEntityFormPage>
  );
};
